import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Award, Check, ChevronRight, ArrowRight, Users, Star, Truck, HeartHandshake, Shield, CheckCircle2, Wrench, Headphones, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import FullWidthHero from '../components/FullWidthHero';
import SEO from '../components/SEO';
import axios from 'axios';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

const HomePage = () => {
  const [spas, setSpas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const spasRes = await axios.get(`${API}/spas`);
        setSpas(spasRes.data.slice(0, 6));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page" className="bg-white">
      <SEO 
        title="Votre projet spa accompagné par un spécialiste | Atlantique Spa Boutique"
        description="Spas sélectionnés, accessoires et pièces détachées livrés en France. Installation et accompagnement personnalisé en Loire-Atlantique (44), Vendée (85), Morbihan (56), Maine-et-Loire (49)."
        canonical="/"
      />

      {/* ============================================= */}
      {/* HERO SECTION - Image plein écran avec texte superposé */}
      {/* ============================================= */}
      <FullWidthHero
        variant="home"
        image={realPhotos.heroMain}
        imageAlt={realPhotosAlt.heroMain}
        badge="Boutique spécialiste du spa"
        badgeIcon={Award}
        title="Votre projet spa accompagné par un"
        titleHighlight="véritable spécialiste"
        subtitle="Spas, pièces détachées et accessoires sélectionnés avec soin. Livraison France entière sur les pièces et accessoires. Installation de spas dans le Grand Ouest."
        primaryCta={{ text: "Découvrir nos spas", href: "/spas", icon: ArrowRight }}
        secondaryCta={{ text: "Parler de mon projet", href: "/contact" }}
        overlayPosition="left"
      />

      {/* ============================================= */}
      {/* TRUST SECTION - Bénéfices clients */}
      {/* ============================================= */}
      <section className="py-24 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pourquoi choisir Atlantique Spa Boutique ?
            </h2>
            <p className="text-lg text-[#475569] max-w-2xl mx-auto leading-relaxed">
              Un accompagnement personnalisé pour concrétiser votre projet spa en toute sérénité.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Trust Block 1 - Conseil */}
            <div className="bg-[#F8FAFC] rounded-xl p-7 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center mb-5">
                <HeartHandshake className="w-7 h-7 text-[#1E3A5F]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] text-lg mb-3">Conseil personnalisé</h3>
              <p className="text-[#475569] text-base leading-relaxed">
                Nous étudions votre espace et vos besoins pour vous orienter vers le spa idéal.
              </p>
            </div>

            {/* Trust Block 2 - Conseil */}
            <div className="bg-[#F8FAFC] rounded-xl p-7 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#10B981]/10 rounded-xl flex items-center justify-center mb-5">
                <Mail className="w-7 h-7 text-[#10B981]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] text-lg mb-3">Besoin d'aide ?</h3>
              <p className="text-[#475569] text-base leading-relaxed">
                Contactez-nous avant commande pour toute question sur les dimensions et références.
              </p>
            </div>

            {/* Trust Block 3 - Garanties */}
            <div className="bg-[#F8FAFC] rounded-xl p-7 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#F97316]/10 rounded-xl flex items-center justify-center mb-5">
                <Shield className="w-7 h-7 text-[#F97316]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] text-lg mb-3">Garanties constructeur</h3>
              <p className="text-[#475569] text-base leading-relaxed">
                Jusqu'à 10 ans sur la structure, 5 ans sur la coque, 2 ans pièces et main-d'œuvre.
              </p>
            </div>

            {/* Trust Block 4 - Installation */}
            <div className="bg-[#F8FAFC] rounded-xl p-7 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 bg-[#1E3A5F]/10 rounded-xl flex items-center justify-center mb-5">
                <Truck className="w-7 h-7 text-[#1E3A5F]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] text-lg mb-3">Installation sur demande</h3>
              <p className="text-[#475569] text-base leading-relaxed">
                Livraison et mise en place par un professionnel, disponible sur demande.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* NATIONAL VS LOCAL - Séparation claire */}
      {/* ============================================= */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Livraison & Zone d'intervention
            </h2>
            <p className="text-[#475569] mt-4 max-w-2xl mx-auto">
              Comprenez en un coup d'œil ce qui est livré partout en France et ce qui nécessite une intervention locale
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-10">
            {/* National - Livraison France */}
            <div className="bg-white rounded-2xl p-8 border-2 border-emerald-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-emerald-500 rounded-xl flex items-center justify-center mb-6">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                🚚 Livraison France entière
              </h3>
              <p className="text-[#475569] text-base leading-relaxed mb-6">
                Commandez en ligne et recevez partout en France métropolitaine :
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-[#0F172A]">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Pièces détachées</span>
                </li>
                <li className="flex items-center gap-3 text-[#0F172A]">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Accessoires spa</span>
                </li>
                <li className="flex items-center gap-3 text-[#0F172A]">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Produits d'entretien</span>
                </li>
                <li className="flex items-center gap-3 text-[#0F172A]">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Pompes à chaleur</span>
                </li>
                <li className="flex items-center gap-3 text-[#0F172A]">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>Couvertures sur mesure</span>
                </li>
              </ul>
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <span className="font-semibold text-emerald-700">Livraison offerte dès 80 € TTC</span>
              </div>
            </div>

            {/* Local - Intervention locale */}
            <div className="bg-[#1E3A5F] rounded-2xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                🔧 Zone d'intervention locale
              </h3>
              <p className="text-white/80 text-base leading-relaxed mb-6">
                Installation, livraison de spas et SAV assurés par nos équipes :
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span>Livraison et mise en place des spas</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span>Installation et raccordement</span>
                </li>
                <li className="flex items-center gap-3 text-white">
                  <Check className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span>SAV et réparation</span>
                </li>
              </ul>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-white/90 text-sm font-medium mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-400" />
                  Nantes & Grand Ouest
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { code: '44', name: 'Loire-Atlantique' },
                    { code: '85', name: 'Vendée' },
                    { code: '56', name: 'Morbihan' },
                    { code: '49', name: 'Maine-et-Loire' }
                  ].map((dept) => (
                    <span key={dept.code} className="bg-white/15 text-white px-3 py-1.5 rounded-lg text-sm font-medium">
                      {dept.code}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* NOTRE EXPERTISE - Section déplacée */}
      {/* ============================================= */}
      <section className="py-24 bg-white" data-testid="expert-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image - Photo terrain réelle du client */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={realPhotos.expertise}
                  alt={realPhotosAlt.expertise}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-5 border border-[#E2E8F0]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                    <Star className="w-6 h-6 text-[#10B981]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0F172A]">+200 spas</div>
                    <div className="text-[#64748B] text-sm">installés depuis 2018</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="inline-flex items-center gap-2 bg-[#1E3A5F]/10 text-[#1E3A5F] px-4 py-2 rounded-full text-sm font-semibold mb-8">
                <Award className="w-4 h-4" />
                Notre expertise
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Une connaissance terrain à votre service
              </h2>
              <p className="text-lg text-[#475569] leading-relaxed mb-8">
                Nous connaissons les spas de l'intérieur. Cette expertise nous permet de vous guider vers les bons choix et d'anticiper vos besoins sur le long terme.
              </p>
              <ul className="space-y-5 mb-10">
                <li className="flex items-start gap-4">
                  <div className="w-7 h-7 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <span className="text-[#475569] text-base leading-relaxed">
                    <strong className="text-[#0F172A]">Sélection rigoureuse</strong> — Des marques et modèles testés et approuvés
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-7 h-7 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <span className="text-[#475569] text-base leading-relaxed">
                    <strong className="text-[#0F172A]">Conseils adaptés</strong> — Orientation selon votre espace, usage et budget
                  </span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-7 h-7 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <span className="text-[#475569] text-base leading-relaxed">
                    <strong className="text-[#0F172A]">Relation directe</strong> — Un seul interlocuteur de A à Z, sans intermédiaire
                  </span>
                </li>
              </ul>
              <Link to="/a-propos">
                <Button className="bg-[#1E3A5F] hover:bg-[#152C4A] text-white rounded-lg h-12 px-6 font-semibold transition-all hover:-translate-y-0.5">
                  En savoir plus
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* SPAS CATALOG PREVIEW */}
      {/* ============================================= */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="spas-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="inline-flex items-center gap-2 bg-[#F97316]/10 text-[#F97316] px-4 py-2 rounded-full text-sm font-semibold mb-5">
                <Star className="w-4 h-4" />
                Notre sélection
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Des spas rigoureusement sélectionnés
              </h2>
            </div>
            <Link to="/spas" className="text-[#1E3A5F] hover:text-[#152C4A] font-semibold flex items-center gap-1 text-base">
              Voir tous les spas
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-10 h-10 border-4 border-[#1E3A5F] border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {spas.map((spa) => (
                <SpaCard key={spa.slug} spa={spa} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================================= */}
      {/* PARTS & ACCESSORIES CTA */}
      {/* ============================================= */}
      <section className="py-20 bg-[#1E3A5F]" data-testid="parts-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Pièces détachées et accessoires
              </h2>
              <p className="text-[#94A3B8] text-lg leading-relaxed mb-8">
                Pompes, filtres, produits de traitement... Découvrez notre sélection de pièces détachées et accessoires pour votre spa.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/pieces-detachees/pompes-circulation">
                  <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 rounded-lg h-11">
                    Pompes de circulation
                  </Button>
                </Link>
                <Link to="/pieces-detachees/pompes-massage">
                  <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 rounded-lg h-11">
                    Pompes de massage
                  </Button>
                </Link>
                <Link to="/accessoires/filtres">
                  <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 rounded-lg h-11">
                    Filtres
                  </Button>
                </Link>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <Link to="/pieces-detachees">
                <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg h-14 px-8 font-semibold transition-all hover:-translate-y-0.5 shadow-lg">
                  Trouver votre pièce
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* FINAL CTA */}
      {/* ============================================= */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Prêt à concrétiser votre projet ?
          </h2>
          <p className="text-lg text-[#475569] mb-10 leading-relaxed">
            Contactez-nous pour une étude gratuite de votre projet spa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg h-14 px-8 font-semibold transition-all hover:-translate-y-0.5 shadow-lg">
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href={`mailto:${EMAIL}`}>
              <Button size="lg" variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5 rounded-lg h-14 px-8 font-semibold">
                <Mail className="w-5 h-5 mr-2" />
                Nous contacter
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* EXPERTISE TECHNIQUE - Section SEO Homepage */}
      {/* ============================================= */}
      <section className="py-16 bg-white border-t border-[#E2E8F0]" data-testid="expertise-section">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Votre spécialiste spa dans le Grand Ouest
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Plus qu'un revendeur, <strong>Atlantique Spa Boutique</strong> vous accompagne de A à Z dans votre projet spa : 
              conseil, installation, maintenance et pièces détachées.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Expertise 1 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#1E3A5F] rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Spécialiste du spa</h3>
              <p className="text-sm text-[#64748B]">
                Expertise reconnue sur les systèmes Balboa, Gecko, LX Whirlpool et Waterway. 
                Nous connaissons les spas et leurs spécificités.
              </p>
            </div>

            {/* Expertise 2 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#10B981] rounded-lg flex items-center justify-center mb-4">
                <Headphones className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Conseil technique</h3>
              <p className="text-sm text-[#64748B]">
                Besoin d'aide pour choisir votre spa ou identifier une pièce ? 
                Notre équipe vous guide avec des conseils personnalisés.
              </p>
            </div>

            {/* Expertise 3 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#F97316] rounded-lg flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Installation Grand Ouest</h3>
              <p className="text-sm text-[#64748B]">
                Service d'installation et SAV spa en Loire-Atlantique (44), 
                Vendée (85), Morbihan (56) et Maine-et-Loire (49).
              </p>
            </div>

            {/* Expertise 4 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-md transition-all">
              <div className="w-12 h-12 bg-[#6366F1] rounded-lg flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Livraison France entière</h3>
              <p className="text-sm text-[#64748B]">
                Pièces détachées et accessoires spa livrés partout en France 
                sous 48-72h. Livraison offerte dès 80€ TTC.
              </p>
            </div>
          </div>

          {/* Link to pieces */}
          <div className="text-center mt-10">
            <Link to="/pieces-accessoires">
              <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white">
                Découvrir nos pièces détachées
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// ============================================= 
// SPA CARD COMPONENT - Miniatures sans rognage
// ============================================= 
const SpaCard = ({ spa }) => {
  const imageUrl = spa.images && spa.images.length > 0 
    ? spa.images[0] 
    : 'https://images.unsplash.com/photo-1572331165267-854da2b10ccc?w=600&q=80';

  return (
    <Link to={`/spas/${spa.slug}`} className="group">
      <Card className="overflow-hidden border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-xl transition-all duration-300 h-full rounded-xl">
        {/* Image container with fixed aspect ratio and contain - NO CROPPING */}
        <div className="aspect-[4/3] bg-white relative overflow-hidden">
          <img 
            src={imageUrl}
            alt={spa.name}
            className="w-full h-full object-contain p-3"
            loading="lazy"
          />
          {/* Gamme badge */}
          {spa.gamme && (
            <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg text-xs font-semibold text-[#1E3A5F] shadow-sm">
              Gamme {spa.gamme}
            </div>
          )}
          {/* Stock badge */}
          {spa.in_stock && (
            <div className="absolute top-3 right-3 bg-[#10B981] text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
              En stock
            </div>
          )}
        </div>
        <CardContent className="p-6">
          <h3 className="font-semibold text-[#0F172A] text-lg mb-3 group-hover:text-[#1E3A5F] transition-colors">
            {spa.name}
          </h3>
          <div className="flex items-center gap-4 text-base text-[#475569] mb-4">
            <span className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              {spa.places} places
            </span>
            {spa.dimensions && (
              <span className="text-sm">{spa.dimensions}</span>
            )}
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-[#E2E8F0]">
            <span className="text-[#1E3A5F] font-semibold">Sur devis</span>
            <span className="text-sm text-[#64748B] group-hover:text-[#1E3A5F] transition-colors flex items-center gap-1 font-medium">
              Voir détails
              <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default HomePage;
